import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import {
    PreviewScene,
    SceneAssets,
    PreviewConfig,
    TemplateId,
    ensureFontLoaded,
    loadImage,
    KEN_BURNS_ZOOM_START,
    KEN_BURNS_ZOOM_END,
    KEN_BURNS_SLIDE_SECONDS,
} from "./previewEngine";
import { BrandColors } from "../../../misc/custom-posts/CaptionedVideoMediaStep";
import { SlideshowText } from "../types";
import {
    SlideshowTemplateId,
    isKenBurnsTemplate,
    isPortraitTemplate,
} from "../slideshowTemplates";

interface PreviewContent {
    template: SlideshowTemplateId;
    brandColors: BrandColors | undefined;
    font: string;
    logo: string | null;
    person: string | undefined;
    firstname: string;
    lastname: string;
    contactNumber: string;
    texts: SlideshowText[];
    // Ordered slide preview URLs (one per selected image, tour order).
    photoUrls: string[];
    // MB-1490: server-resolved recency claim; undefined keeps the badge ON
    // (mirrors the compositor's absent=>ON), only an explicit false hides it.
    recentlyListed?: boolean;
}

const FRAME_LABELS = ["Intro", "Slide", "Closing"] as const;

// Honour the OS "reduce motion" setting: the punch-in still shows, it just does
// not drift.
const prefersReducedMotion = (): boolean =>
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

interface FrameDraw {
    phase: "intro" | "middle" | "outro";
    frame: number;
    photoIndex: number;
}

// Renders ONE frozen frame at a time (intro composite, a middle photo slide, or
// closing composite) with Prev/Next controls to step through them. Frames are
// drawn by porting the marabot-video2 compositor.
const PreviewFrames: React.FC<PreviewContent> = ({
    template,
    brandColors,
    font,
    logo,
    person,
    firstname,
    lastname,
    contactNumber,
    texts,
    photoUrls,
    recentlyListed,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const sceneRef = useRef<PreviewScene | null>(null);
    const assetsRef = useRef<SceneAssets | null>(null);
    const drawsRef = useRef<FrameDraw[]>([]);
    const [ready, setReady] = useState(false);
    const [current, setCurrent] = useState(0);
    // Bumped after every successful (re)build so the draw effect always re-fires
    // — even on a colour-only change where `ready` would otherwise stay true.
    const [renderTick, setRenderTick] = useState(0);

    const isPortrait = isPortraitTemplate(template);
    const isKenBurns = isKenBurnsTemplate(template);

    const colorsKey = brandColors
        ? `${brandColors.main}|${brandColors.secondary}|${brandColors.accent}`
        : "";
    const textsKey = useMemo(
        () => texts.map((t) => `${t.header}${t.subHeader}`).join(""),
        [texts],
    );
    const photosKey = useMemo(() => photoUrls.join(""), [photoUrls]);

    // Build the scene + load assets whenever the config changes.
    useEffect(() => {
        if (template === "video1") return;
        let cancelled = false;
        setReady(false);

        const run = async (): Promise<void> => {
            // Build geometry only after the real font is available — fitText
            // sizing depends on the font's metrics.
            await ensureFontLoaded(font);
            if (cancelled) return;

            const cfg: PreviewConfig = {
                colors: brandColors
                    ? [brandColors.main, brandColors.secondary, brandColors.accent]
                    : undefined,
                font,
                firstname: firstname || "First",
                lastname: lastname || "Last",
                contactNumber: contactNumber || "(555) 555-5555",
                texts,
                imageCount: Math.max(1, photoUrls.length),
                recentlyListed,
            };

            // Load logo + headshot before building the scene: template5 (Modern)
            // sizes its logo plate / watermark from the logo's aspect at build time.
            const [personImg, logoImg] = await Promise.all([
                person ? loadImage(person) : Promise.resolve(undefined),
                logo ? loadImage(logo) : Promise.resolve(undefined),
            ]);
            if (cancelled) return;

            const scene = new PreviewScene(template as TemplateId, cfg, {
                logo: logoImg,
                person: personImg,
            });
            const frames = scene.frames();

            // Load the backdrop photo each frozen frame needs (deduped). template1-4
            // only back the middle slide with a photo; template5 backs all three
            // (first photo for the intro, last for the closing).
            const photoIndices = Array.from(
                new Set([
                    frames.intro.photoIndex,
                    frames.middle.photoIndex,
                    frames.outro.photoIndex,
                ]),
            );
            const loadedPhotos = await Promise.all(
                photoIndices.map((i) => loadImage(photoUrls[i] ?? "")),
            );
            if (cancelled) return;

            const photos: (HTMLImageElement | undefined)[] = [];
            photoIndices.forEach((i, k) => {
                photos[i] = loadedPhotos[k];
            });

            const assets: SceneAssets = { person: personImg, logo: logoImg, photos };

            sceneRef.current = scene;
            assetsRef.current = assets;
            drawsRef.current = [
                { phase: "intro", frame: frames.intro.frame, photoIndex: frames.intro.photoIndex },
                {
                    phase: "middle",
                    frame: frames.middle.frame,
                    photoIndex: frames.middle.photoIndex,
                },
                { phase: "outro", frame: frames.outro.frame, photoIndex: frames.outro.photoIndex },
            ];
            if (!cancelled) {
                setReady(true);
                setRenderTick((t) => t + 1);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        template,
        colorsKey,
        font,
        logo,
        person,
        firstname,
        lastname,
        contactNumber,
        textsKey,
        photosKey,
        recentlyListed,
    ]);

    // Draw the current frame whenever it changes or a (re)build completes.
    useEffect(() => {
        const scene = sceneRef.current;
        const assets = assetsRef.current;
        const draw = drawsRef.current[current];
        const canvas = canvasRef.current;
        if (!scene || !assets || !draw || !canvas) return;
        canvas.width = scene.width;
        canvas.height = scene.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        scene.drawFrame(ctx, draw.frame, assets, draw.phase, draw.photoIndex);
    }, [renderTick, current]);

    // Land on the tour slide for a motion template. It is the only frame that
    // moves, so opening on the frozen intro would show a punched-in still and
    // read as "no animation". Keyed on the template alone, so stepping to another
    // frame afterwards sticks.
    useEffect(() => {
        if (isKenBurnsTemplate(template)) setCurrent(1);
    }, [template]);

    // Ken Burns playback. Only the tour slide moves: video2 freezes the opening
    // slide at ZOOM_START and the closing one at ZOOM_END, so animating either
    // bookend would make the preview LESS faithful. The furniture (scrim,
    // headshot, badge, captions) is redrawn at its same frozen frame every tick —
    // only the photo's scale advances, which is exactly what the effect does.
    useEffect(() => {
        const scene = sceneRef.current;
        const assets = assetsRef.current;
        const draw = drawsRef.current[current];
        const canvas = canvasRef.current;
        if (!scene || !assets || !draw || !canvas) return;
        if (!isKenBurns || draw.phase !== "middle") return;
        if (prefersReducedMotion()) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        let raf = 0;
        let start: number | undefined;
        const tick = (now: number): void => {
            if (start === undefined) start = now;
            const elapsed = ((now - start) / 1000) % KEN_BURNS_SLIDE_SECONDS;
            const progress = elapsed / KEN_BURNS_SLIDE_SECONDS;
            const zoom =
                KEN_BURNS_ZOOM_START +
                (KEN_BURNS_ZOOM_END - KEN_BURNS_ZOOM_START) * progress;
            scene.drawFrame(
                ctx,
                draw.frame,
                assets,
                draw.phase,
                draw.photoIndex,
                zoom,
            );
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [renderTick, current, isKenBurns]);

    const go = (delta: number): void => setCurrent((c) => (c + delta + 3) % 3);

    // Cap the single-frame width so portrait (9:16) doesn't get too tall and
    // landscape (16:9) reads large.
    const frameWidth = isPortrait ? "max-w-[260px]" : "max-w-2xl";

    return (
        <div className={`mx-auto ${frameWidth}`}>
            <div className="relative overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100">
                <canvas ref={canvasRef} className="block h-auto w-full" />
                {!ready && (
                    <div className="absolute inset-0 flex items-center justify-center bg-neutral-100/60">
                        <Loader2 className="h-5 w-5 animate-spin text-secondary" />
                    </div>
                )}
            </div>

            <div className="mt-3 grid grid-cols-3 items-center gap-3">
                <button
                    type="button"
                    onClick={() => go(-1)}
                    disabled={!ready}
                    className="flex items-center gap-1 justify-self-start rounded-md border border-neutral-200 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:border-secondary disabled:opacity-50"
                    aria-label={`Show ${FRAME_LABELS[(current + 2) % 3]}`}
                >
                    <ChevronLeft className="h-4 w-4 text-secondary" />
                    {FRAME_LABELS[(current + 2) % 3]}
                </button>

                <span className="text-center text-xs font-semibold text-primary">
                    {FRAME_LABELS[current]}
                </span>

                <button
                    type="button"
                    onClick={() => go(1)}
                    disabled={!ready}
                    className="flex items-center gap-1 justify-self-end rounded-md border border-neutral-200 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:border-secondary disabled:opacity-50"
                    aria-label={`Show ${FRAME_LABELS[(current + 1) % 3]}`}
                >
                    {FRAME_LABELS[(current + 1) % 3]}
                    <ChevronRight className="h-4 w-4 text-secondary" />
                </button>
            </div>
        </div>
    );
};

// A live, pixel-faithful preview of the V2 slideshow templates: one frame at a
// time, stepped through by slide name.
const SlideshowPreview: React.FC<PreviewContent> = (props) => {
    if (props.template === "video1") return null;

    return (
        <div className="space-y-3">
            <div>
                <h4 className="text-base font-semibold text-primary">Live Preview</h4>
                <p className="text-sm text-neutral-500">
                    Step through the intro, a sample slide, and the closing card —
                    rendered with your colors, font and photos.
                </p>
            </div>

            <PreviewFrames {...props} />
        </div>
    );
};

export default SlideshowPreview;
